import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;

export const loadData = (_params, search) => (dispatch, getState, sdk) => {
  dispatch(fetchOrdersOrSalesRequest());

  const state = getState();
  const currentUserId = state.user?.currentUser?.id?.uuid;

  if (!currentUserId) {
    const error = new Error('Current user ID is missing');
    dispatch(fetchOrdersOrSalesError(error));
    return Promise.reject(error);
  }

  const { page = 1 } = parse(search);

  const apiQueryParams = {
    lastTransitions: getAllTransitionsForEveryProcess(),
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
  };

  const customerQuery = sdk.transactions.query({
    ...apiQueryParams,
    customerId: currentUserId,
  });

  const providerQuery = sdk.transactions.query({
    ...apiQueryParams,
    providerId: currentUserId,
  });

  return Promise.all([customerQuery, providerQuery])
    .then(([ordersResponse, salesResponse]) => {
      // Combine orders and sales
      const combined = [...ordersResponse.data.data, ...salesResponse.data.data];
      // Use a Map to remove duplicates
      const uniqueTransactionsMap = new Map();
      combined.forEach(tx => {
        uniqueTransactionsMap.set(tx.id.uuid, tx);
      });


      const uniqueTransactions = Array.from(uniqueTransactionsMap.values());
      const combinedTransactions = {
        data: {
          data: uniqueTransactions,
          meta: {
            totalItems:
              (ordersResponse.meta?.totalItems || 0) +
              (salesResponse.meta?.totalItems || 0),
            totalPages: 1, // Consider improving pagination later
          },
        },
      };

      dispatch(addMarketplaceEntities(ordersResponse));
      dispatch(addMarketplaceEntities(salesResponse));
      dispatch(fetchOrdersOrSalesSuccess(combinedTransactions));
      return combinedTransactions;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};

